import { Controller } from "@hotwired/stimulus";

// Opens the preview of a MONSTRO, MAGIA or EQUIPAMENTO link in a chapter (docs/architecture/catalogue.md, "The book
// preview"). A plain click opens a popover beside the link from md and the bottom sheet below; a modified click and
// a browser without the Popover API follow the link, which is also all there is without script.
//
// The fragment is the record's own page with `?previa=1`, fetched per reader (the chapter itself is cached for
// everyone) and kept per URL for the life of the page.
const DESKTOP = "(min-width: 48rem)";
const GAP = 6;
const MARGIN = 8;

type ModalOutlet = { open(): void };

export default class extends Controller<HTMLElement> {
	static targets = ["popover", "sheet", "body"];
	static outlets = ["modal"];
	declare readonly popoverTarget: HTMLElement;
	declare readonly sheetTarget: HTMLDialogElement;
	declare readonly bodyTargets: HTMLElement[];
	declare readonly hasModalOutlet: boolean;
	declare readonly modalOutlet: ModalOutlet;

	#loading = "";
	#loadingLabel = "";
	#opener: HTMLAnchorElement | null = null;
	// The open request's generation: it only grows, a dismissal or a new open bumps it, so an answer that arrives
	// after either, or after a visit elsewhere, no longer matches and is dropped.
	#request = 0;
	#fragments = new Map<string, Promise<string | null>>();
	#html = new Map<string, string>();
	#media = window.matchMedia(DESKTOP);
	#frame = 0;
	#follow = () => {
		if (this.#frame) return;
		this.#frame = requestAnimationFrame(() => {
			this.#frame = 0;
			this.#place();
		});
	};
	#closeAll = () => {
		this.#request++;
		if (this.popoverTarget.matches(":popover-open")) this.popoverTarget.hidePopover();
		if (this.sheetTarget.open) this.sheetTarget.close();
	};

	// What Turbo caches is the chapter for the next reader of this browser, so nothing fetched stays in it: the bodies go
	// back to the loading line, and what was fetched for this reader is forgotten.
	#reset = () => {
		this.#closeAll();
		this.#opener = null;
		this.#fragments.clear();
		this.#html.clear();
		for (const body of this.bodyTargets) body.innerHTML = this.#loading;
		this.#label(this.#loadingLabel);
	};

	connect() {
		this.#loading = this.bodyTargets[0]?.innerHTML ?? "";
		this.#loadingLabel = this.popoverTarget.getAttribute("aria-label") ?? "";
		this.#media.addEventListener("change", this.#closeAll);
		document.addEventListener("turbo:before-cache", this.#reset);
	}

	disconnect() {
		this.#media.removeEventListener("change", this.#closeAll);
		document.removeEventListener("turbo:before-cache", this.#reset);
		this.#reset();
	}

	open(event: MouseEvent) {
		const link = this.#link(event);
		if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		if (!this.#supported()) return;

		event.preventDefault();
		this.#closeAll();
		this.#opener = link;
		const request = ++this.#request;
		const url = this.#url(link);
		this.#show(this.#html.get(url), link.textContent ?? "");
		void this.#fetch(url).then((html) => {
			if (this.#request !== request) return;
			if (html === null) {
				this.#closeAll();
				window.location.assign(link.href);
				return;
			}
			this.#fill(html);
		});
	}

	// Reading the fragment ahead is cheap, so a hover or a keyboard visit warms it for the click that may follow.
	prefetch(event: Event) {
		const link = this.#link(event);
		if (link && this.#supported()) void this.#fetch(this.#url(link));
	}

	closePopover() {
		this.popoverTarget.hidePopover();
	}

	// Escape, a click outside and the close button all end here: the focus goes back to the link, unless the reader
	// already moved it somewhere else.
	popoverToggled(event: Event) {
		if ((event as ToggleEvent).newState === "open") {
			window.addEventListener("scroll", this.#follow, { passive: true });
			window.addEventListener("resize", this.#follow);
		} else {
			window.removeEventListener("scroll", this.#follow);
			window.removeEventListener("resize", this.#follow);
			if (!this.popoverTarget.matches(":popover-open")) this.#request++;
			this.#restoreFocus(this.popoverTarget);
		}
	}

	sheetClosed() {
		if (!this.sheetTarget.open) this.#request++;
		this.#restoreFocus(this.sheetTarget);
	}

	#link(event: Event): HTMLAnchorElement | null {
		const link = (event.target as Element | null)?.closest<HTMLAnchorElement>("a[data-preview-type]");
		return link && this.element.contains(link) ? link : null;
	}

	#supported() {
		return "showPopover" in HTMLElement.prototype && (this.#media.matches || this.hasModalOutlet);
	}

	#url(link: HTMLAnchorElement) {
		const url = new URL(link.href, window.location.href);
		url.hash = "";
		url.searchParams.set("previa", "1");
		return url.toString();
	}

	#fetch(url: string) {
		let pending = this.#fragments.get(url);
		if (!pending) {
			pending = fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
				.then((response) => (response.ok ? response.text() : null))
				.catch(() => null);
			this.#fragments.set(url, pending);
			void pending.then((html) => {
				if (this.#fragments.get(url) !== pending) return;
				if (html === null) this.#fragments.delete(url);
				else this.#html.set(url, html);
			});
		}
		return pending;
	}

	// The container opens at once, with the fragment when it is already here and the loading line otherwise.
	#show(html: string | undefined, label: string) {
		for (const body of this.bodyTargets) body.innerHTML = html ?? this.#loading;
		this.#label(label);
		if (this.#media.matches) {
			this.popoverTarget.showPopover();
			this.#place();
		} else {
			this.modalOutlet.open();
		}
	}

	#fill(html: string) {
		for (const body of this.bodyTargets) body.innerHTML = html;
		this.#label(this.bodyTargets[0]?.querySelector<HTMLElement>("[data-preview-label]")?.dataset.previewLabel);
		this.#place();
		const container = this.#media.matches ? this.popoverTarget : this.sheetTarget;
		container.querySelector<HTMLElement>(".odo-preview a")?.focus({ preventScroll: true });
	}

	#label(text?: string) {
		if (!text) return;
		this.popoverTarget.setAttribute("aria-label", text);
		this.sheetTarget.setAttribute("aria-label", text);
	}

	// Below the link, or above it when there is no room; inside the viewport on both axes (the popover scrolls past the
	// viewport's height). The popover is in the top layer, so these are viewport coordinates, and a scroll moves it with
	// the link.
	#place() {
		const link = this.#opener;
		const popover = this.popoverTarget;
		if (!link || !popover.matches(":popover-open")) return;

		const rect = link.getClientRects()[0] ?? link.getBoundingClientRect();
		if (rect.bottom < 0 || rect.top > window.innerHeight) {
			popover.hidePopover();
			return;
		}
		const { width, height } = popover.getBoundingClientRect();
		const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - width - MARGIN));
		const below = rect.bottom + GAP;
		const above = rect.top - GAP - height;
		const preferred = below + height + MARGIN > window.innerHeight && above >= MARGIN ? above : below;
		const top = Math.max(MARGIN, Math.min(preferred, window.innerHeight - height - MARGIN));
		popover.style.left = `${left}px`;
		popover.style.top = `${top}px`;
	}

	#restoreFocus(container: HTMLElement) {
		const active = document.activeElement;
		if (!active || active === document.body || container.contains(active)) this.#opener?.focus({ preventScroll: true });
	}
}
